import { AxiosResponse } from 'axios';
import { BaseAPI } from 'apis/amazon';

export type UsageSummaryMetrics = {
  totalHoursTranscribed: number;
  apiCalls: number;
  totalComputeTimeMinutes: number;
  totalComputeTimeSeconds: number;
};

export type DetailedUsage = {
  id: string;
  apiName: string;
  date: string;
  fileName: string;
  contentType: string | null;
  contentCategory: 'audio' | 'video' | null;
  fileSize: string;
  fileSizeMb: number;
  duration: string;
  durationMinutes: number;
  durationSeconds: number;
  computeTime: string | null;
  computeTimeMinutes: number | null;
  computeTimeSeconds: number | null;
  confidenceScore?: string;
};

type UsageSummaryResponse = {
  data: UsageSummaryMetrics;
};

type UsageTrackingResponse = {
  data: DetailedUsage[];
  total: number;
};

// API response structure from the backend
type ApiResponseData<T> = {
  href: string;
  data: T;
  total?: number;
};

// Raw API response type (before transformation) - allows "NULL" strings
type RawDetailedUsage = {
  id: string;
  apiName: string;
  date: string;
  fileName: string;
  contentType: string | 'NULL';
  contentCategory: 'audio' | 'video' | 'NULL' | null;
  fileSize: string;
  fileSizeMb: number;
  duration: string;
  durationMinutes: number;
  durationSeconds: number;
  computeTime: string | 'NULL' | null;
  computeTimeMinutes: number | null;
  computeTimeSeconds: number | null;
  confidenceScore?: string;
};

/**
 * Transforms the API response for usage summary to the service response format
 * @param apiResponse - The raw API response containing usage summary metrics
 * @returns Transformed response with normalized data structure
 */
const transformSummaryResponse = (
  apiResponse: AxiosResponse<ApiResponseData<UsageSummaryMetrics>>,
): AxiosResponse<UsageSummaryResponse> => {
  const summaryData = apiResponse.data.data;
  return {
    ...apiResponse,
    data: {
      data: summaryData,
    },
  };
};

/**
 * Normalizes NULL string values from API to null
 * @param value - The value that may be 'NULL' string, null, or the actual value
 * @returns null if value is 'NULL' or null, otherwise returns the value
 */
const normalizeNullValue = <T>(value: T | 'NULL' | null): T | null => {
  return value === 'NULL' || value === null ? null : value;
};

/**
 * Normalizes content category from API format to service format
 * @param category - The content category from API (may be 'NULL' string)
 * @returns Normalized content category or null
 */
const normalizeContentCategory = (
  category: 'audio' | 'video' | 'NULL' | null,
): 'audio' | 'video' | null => {
  if (category === 'NULL' || category === null) {
    return null;
  }
  return category as 'audio' | 'video';
};

/**
 * Transforms a single raw usage item from API format to service format
 * @param item - Raw usage item from API
 * @returns Transformed detailed usage item
 */
const transformUsageItem = (item: RawDetailedUsage): DetailedUsage => {
  return {
    ...item,
    contentCategory: normalizeContentCategory(item.contentCategory),
    contentType: normalizeNullValue(item.contentType),
    computeTime: normalizeNullValue(item.computeTime),
    computeTimeMinutes: item.computeTimeMinutes ?? null,
  };
};

/**
 * Transforms the API response for detailed usage tracking to the service response format
 * Handles NULL string values and normalizes data types
 * @param apiResponse - The raw API response containing detailed usage data
 * @returns Transformed response with normalized data structure
 */
const transformDetailedResponse = (
  apiResponse: AxiosResponse<ApiResponseData<RawDetailedUsage[]>>,
): AxiosResponse<UsageTrackingResponse> => {
  const rawData = apiResponse.data.data;
  const detailedData: DetailedUsage[] = rawData.map(transformUsageItem);

  return {
    ...apiResponse,
    data: {
      data: detailedData,
      total: apiResponse.data.total ?? detailedData.length,
    },
  };
};

export const UsageTrackingService = {
  getUsageSummary: async (): Promise<AxiosResponse<UsageSummaryResponse>> => {
    try {
      const response = await BaseAPI.get<ApiResponseData<UsageSummaryMetrics>>(
        '/usage-tracking/summary',
      );
      return transformSummaryResponse(response);
    } catch (error) {
      console.error('Error fetching usage summary:', error);
      throw error;
    }
  },

  getUsageTracking: async (): Promise<AxiosResponse<UsageTrackingResponse>> => {
    try {
      const response = await BaseAPI.get<ApiResponseData<RawDetailedUsage[]>>(
        '/usage-tracking/detailed',
      );
      return transformDetailedResponse(response);
    } catch (error) {
      console.error('Error fetching detailed usage tracking:', error);
      throw error;
    }
  },
};
